/* eslint max-lines: ["error", {"max": 800, "skipComments": true, "skipBlankLines": true }] */
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  MenuItem,
  Radio,
  Typography,
} from '@mui/material';
import { format, subYears } from 'date-fns';
import { first, isNil, map, noop, toLower } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form, useFormState } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { addPatientApi, validateAddressApi } from 'api/patient';
import gender from 'common/constants/gender';
import { nameRegex } from 'common/constants/regex';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import DateField from 'common/forms/DateField';
import {
  validateCombinedAddressLength,
  validateEmail,
  validateMaxDate,
  validatePhoneNumber,
  validateValueWithRegex,
  validateZipCode,
} from 'common/forms/formValidations';
import RadioGroupField from 'common/forms/RadioGroupField';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import { handleError } from 'store/thunks/errorHandlerThunks';

const AddressConfirmation = ({ addresses, onConfirm, onClose, patient, isInvalidAddress }) => {
  const handleUpdate = ({ address }) =>
    onConfirm({
      patient: {
        ...patient,
        address: JSON.parse(address),
        ...(addresses?.length === 1 && { isManuallyVerified: true }),
      },
    });

  return (
    <Dialog open={!isNil(addresses)}>
      <DialogTitle>Confirm Address</DialogTitle>

      <Form
        onSubmit={handleUpdate}
        render={({ handleSubmit, invalid, submitting }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent>
              {isInvalidAddress && (
                <Typography sx={{ mb: 3 }}>
                  We were unable to verify the entered address. Please review the address carefully
                  and confirm that you have verified its accuracy before proceeding.
                </Typography>
              )}
              <RadioGroupField
                name='address'
                id='AddressConfirmation-address-radioGroup'
                label='Please confirm the patient address'
                required
              >
                {map(addresses, (address, index) => (
                  <FormControlLabel
                    value={JSON.stringify(address)}
                    key={index}
                    control={<Radio />}
                    label={`${address.line1}, ${address.line2 ? `${address.line2}, ` : ''}${
                      address.city
                    }, ${address.state} ${address.zip}`}
                  />
                ))}
              </RadioGroupField>
            </DialogContent>

            <DialogActions>
              <Button variant='outlined' onClick={onClose} disabled={submitting}>
                Cancel
              </Button>

              <LoadingButton
                variant='contained'
                sx={{ width: 100 }}
                type='submit'
                disabled={invalid || submitting}
                loading={submitting}
              >
                Confirm
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

AddressConfirmation.propTypes = {
  patient: PropTypes.shape({
    address: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      state: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
    }).isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
  }),
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      state: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
    })
  ),
  isInvalidAddress: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

AddressConfirmation.defaultProps = {
  addresses: null,
  patient: null,
};

const CreateNewPatientButton = ({ onCreatePatient }) => {
  const dispatch = useDispatch();
  const patientMinAge = Number(process.env.REACT_APP_PATIENT_MIN_AGE) || 10;
  const [addresses, setAddresses] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [isInvalidAddress, setInValidAddress] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const formState = useFormState();

  const handleOpen = () => setOpenDialog(true);
  const handleClose = () => setOpenDialog(false);

  const handleCreatePatient = ({ patient }) => {
    return addPatientApi({
      patientData: {
        firstName: patient.firstName,
        lastName: patient.lastName,
        dob: format(patient.dob, 'yyyy-MM-dd'),
        gender: patient.gender,
        addressLine1: patient.address.line1,
        addressLine2: patient.address.line2,
        city: patient.address.city,
        state: patient.address.state,
        zip: patient.address.zip,
        email: patient.email,
        mobilePhone: patient.phoneNumber,
        isVerifiedAddress: true,
        isManuallyVerified: patient.isManuallyVerified ?? false,
        partners: [
          {
            partnerUUID: formState.values.partnerId,
            isOriginalPartner: true,
          },
        ],
        practice: {
          practiceId: formState.values.practiceId,
        },
      },
    })
      .then((createdPatient) => {
        onCreatePatient({ patient: createdPatient });
        handleClose();
        setAddresses(null);
        enqueueSnackbar('Patient profile created successfully', { variant: 'success' });
      })
      .catch((error) => dispatch(handleError({ error })));
  };

  const handleAddressValidation = (patient) => {
    return validateAddressApi({ input: patient.address })
      .then((addressPayload) => {
        if (addressPayload.length === 1) {
          const addressFromApi = first(addressPayload);

          if (
            toLower(addressFromApi?.line1) === toLower(patient.address.line1) &&
            toLower(addressFromApi?.line2) === toLower(patient.address.line2) &&
            toLower(addressFromApi?.city) === toLower(patient.address.city) &&
            toLower(addressFromApi?.state) === toLower(patient.address.state) &&
            toLower(addressFromApi?.zip) === toLower(patient.address.zip)
          ) {
            return handleCreatePatient({ patient });
          }
        }

        setAddresses(addressPayload);
        setPatientInfo(patient);
        return undefined;
      })
      .catch(() => {
        setPatientInfo(patient);
        setInValidAddress(true);
        setAddresses([patient.address]);
      });
  };

  const clearAddresses = () => setAddresses(null);

  return (
    <>
      <AddressConfirmation
        patient={patientInfo}
        addresses={addresses}
        onConfirm={handleCreatePatient}
        onClose={clearAddresses}
        isInvalidAddress={isInvalidAddress}
      />
      <Button variant='contained' onClick={handleOpen}>
        Create new patient profile
      </Button>
      <Dialog open={openDialog} maxWidth='lg' fullWidth>
        <DialogTitle>New Patient</DialogTitle>
        <Form
          onSubmit={handleAddressValidation}
          render={({ handleSubmit, submitting, invalid }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container columnSpacing={1}>
                  <Grid xs={6}>
                    <Box>
                      <TextField
                        id='NewPatientDialog-firstName-input'
                        label='First Name'
                        name='firstName'
                        required
                        disabled={submitting}
                        validations={[validateValueWithRegex('Invalid Name', nameRegex)]}
                      />

                      <TextField
                        id='NewPatientDialog-lastName-input'
                        label='Last Name'
                        name='lastName'
                        required
                        disabled={submitting}
                        validations={[validateValueWithRegex('Invalid Name', nameRegex)]}
                      />

                      <Box>
                        <Grid container columnSpacing={1}>
                          <Grid xs={6}>
                            <DateField
                              id='NewPatientDialog-dob-input'
                              label='Date of Birth'
                              name='dob'
                              minDate={new Date('1900-01-01')}
                              maxDate={subYears(new Date(), patientMinAge)}
                              required
                              disabled={submitting}
                              validations={[
                                validateMaxDate(
                                  `Patient must be at least ${patientMinAge} years old`,
                                  subYears(new Date(), patientMinAge)
                                ),
                              ]}
                            />
                          </Grid>

                          <Grid xs={6}>
                            <SelectField
                              id='NewPatientDialog-gender-input'
                              label='Gender'
                              name='gender'
                              required
                              disabled={submitting}
                            >
                              {map(gender, (label, value) => (
                                <MenuItem value={value} key={value}>
                                  {label}
                                </MenuItem>
                              ))}
                            </SelectField>
                          </Grid>
                        </Grid>
                      </Box>

                      <TextField
                        id='NewPatientDialog-phoneNumber-input'
                        label='Phone Number'
                        name='phoneNumber'
                        required
                        validations={[validatePhoneNumber()]}
                        disabled={submitting}
                      />

                      <TextField
                        id='NewPatientDialog-email-input'
                        label='Email'
                        name='email'
                        required
                        validations={[validateEmail()]}
                        disabled={submitting}
                      />
                    </Box>
                  </Grid>
                  <Divider orientation='vertical' flexItem />

                  <Grid xs={5.9}>
                    <TextField
                      id='NewPatientDialog-addressLine1-input'
                      label='Address Line 1'
                      name='address.line1'
                      required
                      disabled={submitting}
                      validations={[
                        validateCombinedAddressLength('address.line1', 'address.line2'),
                      ]}
                    />

                    <TextField
                      id='NewPatientDialog-addressLine2-input'
                      label='Address Line 2'
                      name='address.line2'
                      disabled={submitting}
                      validations={[
                        validateCombinedAddressLength('address.line1', 'address.line2'),
                      ]}
                    />

                    <TextField
                      id='NewPatientDialog-city-input'
                      label='City'
                      name='address.city'
                      required
                      disabled={submitting}
                    />

                    <SelectField
                      id='NewPatientDialog-state-dropdown'
                      label='State'
                      name='address.state'
                      required
                      disabled={submitting}
                    >
                      {map(usStateAbbreviation, ({ name, abbreviation }) => (
                        <MenuItem value={abbreviation} key={abbreviation}>
                          {name}
                        </MenuItem>
                      ))}
                    </SelectField>

                    <TextField
                      id='NewPatientDialog-zip-input'
                      label='Zip'
                      name='address.zip'
                      required
                      disabled={submitting}
                      validations={[validateZipCode('Must be valid zip')]}
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} variant='outlined' disabled={submitting}>
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  type='submit'
                  disabled={invalid || submitting}
                  loading={submitting}
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

CreateNewPatientButton.propTypes = {
  onCreatePatient: PropTypes.func,
};

CreateNewPatientButton.defaultProps = {
  onCreatePatient: noop,
};

export default CreateNewPatientButton;
